import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-qr-code.css?inline';

/**
 * `<pk-qr-code>` — lightweight wrapper to display a server-rendered QR code image.
 * The server generates the data URI; this component handles sizing, styling, and ARIA.
 *
 * Attributes:
 *   - `src`   — data URI (or URL) of the QR code image (required for anything to show)
 *   - `label` — accessible label for the image (default: "QR code")
 *   - `size`  — pixel width/height of the rendered box (default: 200)
 */
export class PkQrCode extends HTMLElement {
    static readonly tagName = 'pk-qr-code';

    static get observedAttributes(): string[] {
        return ['src', 'label', 'size'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private get _src(): string {
        return this.getAttribute('src') ?? '';
    }

    private get _label(): string {
        return this.getAttribute('label') ?? 'QR code';
    }

    private get _size(): number {
        const raw = Number.parseInt(this.getAttribute('size') ?? '200', 10);
        return Number.isNaN(raw) ? 200 : raw;
    }

    private template(): TemplateResult {
        const src   = this._src;
        const label = this._label;
        const size  = this._size;

        return html`
            <style>${styles}</style>
            <div class="wrapper" style="width:${size}px;height:${size}px;">
                ${src
                    ? html`<img
                            class="qr-img"
                            src=${src}
                            alt=${label}
                            aria-label=${label}
                            width=${size}
                            height=${size}
                        />`
                    : html`<div class="placeholder" aria-label=${label} role="img"
                                style="width:${size}px;height:${size}px;"></div>`
                }
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkQrCode.tagName)) {
    customElements.define(PkQrCode.tagName, PkQrCode);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-qr-code': PkQrCode;
    }
}
